import React, { useEffect, useState } from 'react';
import { getBackendUrl } from '@/config';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { StatsCard } from '@/components/shared/StatsCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Briefcase, FileText, Upload, Clock, ArrowRight, DollarSign, Plus, Eye, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function BusinessDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    estimated_tax: 0,
    filing_status: 'not_started',
    documents_uploaded: 0,
    total_filings: 0,
    days_remaining: 0
  });
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Handle starting new business filing
  const handleStartNewFiling = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmNewFiling = () => {
    setShowConfirmDialog(false);
    navigate('/business/filing/start?new=true');
  };

  const handleCancelNewFiling = () => {
    setShowConfirmDialog(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const backendUrl = getBackendUrl();
        const token = localStorage.getItem('access_token');
        
        // Fetch business filings
        const response = await axios.get(`${backendUrl}/api/tax-filings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const allFilings = response.data || [];
        // Filter for business filings only
        const businessFilings = allFilings.filter(f => f.filing_type === 'business');
        
        // Sort by updated_at descending (newest first)
        businessFilings.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
        
        // Group filings by business name to show only latest version of each
        const filingsByBusiness = {};
        businessFilings.forEach(filing => {
          const businessName = filing.personal_info?.business_name || 'Unnamed Business';
          const key = `${filing.tax_year}-${businessName}`;
          
          if (!filingsByBusiness[key]) {
            filingsByBusiness[key] = filing;
          } else {
            // If we already have a submitted version, keep it and ignore drafts
            const existing = filingsByBusiness[key];
            if (existing.status === 'DRAFT' && filing.status !== 'DRAFT') {
              // Replace draft with submitted version
              filingsByBusiness[key] = filing;
            }
            // Otherwise keep the existing one (which is newer due to sorting)
          }
        });
        
        const uniqueFilings = Object.values(filingsByBusiness);
        setFilings(uniqueFilings);
        
        // Set stats
        const submittedCount = uniqueFilings.filter(f => f.status !== 'DRAFT').length;
        const draftCount = uniqueFilings.filter(f => f.status === 'DRAFT').length;
        
        setStats({
          estimated_tax: uniqueFilings.reduce((sum, f) => sum + (f.estimated_refund || 0), 0),
          filing_status: submittedCount > 0 ? 'submitted' : (draftCount > 0 ? 'draft' : 'not_started'),
          documents_uploaded: 0,
          total_filings: uniqueFilings.length,
          days_remaining: 0
        });
        
      } catch (error) {
        console.error('Error fetching business data:', error);
        setStats({
          estimated_tax: 0,
          filing_status: 'not_started',
          documents_uploaded: 0,
          total_filings: 0,
          days_remaining: 0
        });
        setFilings([]);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Handle filing click - navigate to view or edit based on status
  const handleFilingClick = (filing) => {
    const isEditable = filing.status === 'DRAFT' || filing.status === 'NEED_INFO';
    if (isEditable) {
      navigate(`/business/filing/start?id=${filing.id}`);
    } else {
      navigate(`/business/filing/start?id=${filing.id}&mode=view`);
    }
  };

  // Get status badge color
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-500';
      case 'SUBMITTED': return 'bg-blue-500';
      case 'NEED_INFO': return 'bg-yellow-500';
      case 'READY_FOR_REVIEW': return 'bg-green-500';
      case 'SUBMITTED_TO_IRS': return 'bg-green-600';
      default: return 'bg-gray-500';
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Business Dashboard" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="Business Dashboard" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Business Dashboard' }]} />

        {/* Filing Type Badge */}
        <div className="flex items-center gap-3">
          <Badge className="bg-primary text-primary-foreground px-4 py-2">
            <Briefcase className="h-4 w-4 mr-2" />
            Business Filing
          </Badge>
        </div>

        {/* Welcome Section */}
        <div>
          <h2 className="text-3xl font-bold text-foreground">Business Tax Filing Dashboard</h2>
          <p className="mt-1 text-muted-foreground">Manage your business tax returns</p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            title="Estimated Tax"
            value={`$${stats.estimated_tax.toLocaleString()}`}
            icon={DollarSign}
            iconBgColor="bg-primary"
            iconColor="text-primary-foreground"
          />
          <StatsCard
            title="Filing Status"
            value={stats.filing_status.replace('_', ' ').toUpperCase()}
            icon={FileText}
            iconBgColor="bg-warning"
            iconColor="text-warning-foreground"
          />
          <StatsCard
            title="Total Filings"
            value={`${stats.total_filings}`}
            icon={Briefcase}
            iconBgColor="bg-info"
            iconColor="text-info-foreground"
          />
          <StatsCard
            title="Days Remaining"
            value={stats.days_remaining.toString()}
            icon={Clock}
            iconBgColor="bg-accent"
            iconColor="text-accent-foreground"
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Quick Actions */}
          <Card className="tax-card lg:col-span-1">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                type="button"
                className="w-full justify-between" 
                onClick={handleStartNewFiling}
              >
                <span className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Start New Business Filing
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-between"
                onClick={() => navigate('/business/documents')}
              >
                Upload Documents
                <Upload className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-between"
                onClick={() => navigate('/business/filing-status')}
              >
                Check Filing Status
                <FileText className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          {/* Recent Activity / All Filings */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Your Business Filings</CardTitle>
                {filings.length > 2 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/business/requests')}
                  >
                    View All
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {filings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No business filings yet</p>
                  <p className="text-sm mt-2">Start a new business filing to get started</p>
                  <Button 
                    type="button"
                    className="mt-4"
                    onClick={handleStartNewFiling}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Start New Filing
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {filings.slice(0, 2).map((filing) => {
                    const isEditable = filing.status === 'DRAFT' || filing.status === 'NEED_INFO' || filing.status === 'SUBMITTED';
                    const businessName = filing.personal_info?.business_name || 'Unnamed Business';
                    
                    return (
                      <div
                        key={filing.id}
                        className="flex flex-col p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => handleFilingClick(filing)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Briefcase className="h-4 w-4 text-primary" />
                              <p className="font-medium">{businessName}</p>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              Tax Year {filing.tax_year} • Updated {new Date(filing.updated_at || filing.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={`${getStatusBadgeClass(filing.status)} text-white`}>
                              {filing.status?.replace('_', ' ')}
                            </Badge>
                            <Button variant="ghost" size="sm">
                              {isEditable ? (
                                <Edit className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                        {/* Agent Info */}
                        {filing.agent_name && (
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              {filing.agent_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-xs text-blue-600 dark:text-blue-400">Your Tax Professional</p>
                              <p className="text-sm font-medium">{filing.agent_name}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Business Specific Info Card */}
        <Card className="bg-gradient-to-br from-primary/5 to-secondary/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-primary/10 p-3">
                <Briefcase className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">
                  Business Tax Filing Requirements
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Ensure you have the following documents ready for your business tax filing:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                    Profit & Loss Statement
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                    Balance Sheet
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                    Business Expense Records
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                    Employee W-2 Forms (if applicable)
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog for New Business Filing */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Certification & Consent - Business Tax Filing</AlertDialogTitle>
            <div className="space-y-4 text-sm text-left">
              <p className="font-medium text-foreground">
                Please read and acknowledge the following before proceeding:
              </p>
              
              <div className="space-y-3 bg-muted/50 p-4 rounded-lg border text-muted-foreground">
                <p>
                  <span className="font-medium">1.</span> I certify that all documents and information I submit are true, complete, and accurate to the best of my knowledge.
                </p>
                <p>
                  <span className="font-medium">2.</span> I authorize Kosha Tax Consultants to review, prepare, and file my tax return using the information I provide.
                </p>
                <p>
                  <span className="font-medium">3.</span> I understand that my documents contain sensitive personal and financial data and consent to their secure storage and processing.
                </p>
                <p>
                  <span className="font-medium">4.</span> I acknowledge that tax refunds, credits, and outcomes are determined by the IRS and applicable authorities and are not guaranteed.
                </p>
                <p>
                  <span className="font-medium">5.</span> I agree to respond promptly to requests for additional information. Delays caused by incomplete or missing documents are my responsibility.
                </p>
                <p>
                  <span className="font-medium">6.</span> I understand that Kosha Tax Consultants is not responsible for penalties, interest, or losses resulting from incorrect, incomplete, or misleading information provided by me.
                </p>
                <p>
                  <span className="font-medium">7.</span> I consent to receive tax-related communications and documents electronically.
                </p>
                <p>
                  <span className="font-medium">8.</span> I agree that submitted documents may be retained for compliance, audit, and regulatory purposes.
                </p>
                <p>
                  <span className="font-medium">9.</span> I understand that submission of documents does not establish a client relationship until services are confirmed and payment is completed.
                </p>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="font-medium text-blue-900 dark:text-blue-100 mb-2">Data Privacy & Deletion Request</p>
                <p className="text-blue-800 dark:text-blue-200">
                  I understand that I may request deletion of my personal data, subject to legal and regulatory retention requirements, by contacting <a href="mailto:support@koshatax.com" className="underline font-medium">support@koshatax.com</a>.
                </p>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel onClick={handleCancelNewFiling}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmNewFiling}>I Agree & Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
